import React, { useState } from "react";
import { TrendingUp, ShoppingCart, Target, ArrowUpRight, Info, Wallet, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface CouponOrder {
  id: number;
  number: string;
  date_created: string;
  total: number;
  shipping_total: number;
  status: string;
}

interface CouponAnalysis {
  role?: string;
  // Rate the API resolved for this coupon/month. 0.15 once the influencer has
  // made a month's top 5, otherwise the 10% base rate.
  commission_rate?: number;
  summary: {
    total_sales: number;
    average_order_value: number;
  };
  orders: {
    all: CouponOrder[];
    completed: CouponOrder[];
    other: CouponOrder[];
  };
}

interface DashboardStatsProps {
  couponAnalysis: CouponAnalysis | null;
  couponLoading: boolean;
  formatCurrency: (value: number) => string;
  calculateCommission: (totalSales: number, shippingFees: number, storedRate?: number | null) => number;
  getCommissionPercentage: (storedRate?: number | null) => string;
  getTierName: (storedRate?: number | null) => string;
  isTopPerformerRate: (storedRate?: number | null) => boolean;
  onNavigateToWallet: () => void;
  availableBalance: number | null;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({
  couponAnalysis,
  couponLoading,
  formatCurrency,
  calculateCommission,
  getCommissionPercentage,
  getTierName,
  isTopPerformerRate,
  onNavigateToWallet,
  availableBalance,
}) => {
  const [commissionHelpOpen, setCommissionHelpOpen] = useState(false);

  const commissionRate = couponAnalysis?.commission_rate;

  const getCommissionTooltip = () => {
    if (!couponAnalysis) return "";

    const total = couponAnalysis.summary.total_sales ?? 0;
    const shipping = couponAnalysis.orders.completed.reduce((sum, order) => sum + (order.shipping_total || 0), 0);
    const totalFees = shipping; // Only shipping
    const commission = calculateCommission(total, shipping, commissionRate);
    const commissionPercentage = getCommissionPercentage(commissionRate);

    return `Comissão: (Total: ${formatCurrency(total)} - Frete: ${formatCurrency(
      totalFees
    )}) × ${commissionPercentage} = ${formatCurrency(commission)}`;
  };

  const getTierTooltip = () => {
    const percentage = getCommissionPercentage(commissionRate);

    if (isTopPerformerRate(commissionRate)) {
      return `Top 5 (${percentage}): você entrou no top 5 do mês e essa taxa é sua para sempre. Parabéns!`;
    }
    return `Comissão padrão (${percentage}): fique entre os 5 maiores vendedores do mês para subir para 15% de forma permanente.`;
  };

  return (
    <TooltipProvider>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
        {/* Total Sales Card */}
        <Card className="relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 depth-2 depth-transition depth-hover-3">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Faturamento</CardTitle>
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {couponLoading && !couponAnalysis ? (
            <div className="space-y-2">
              <div className="h-8 w-24 bg-muted rounded animate-pulse"></div>
              <div className="h-3 w-32 bg-muted rounded animate-pulse"></div>
            </div>
          ) : (
            <>
              <div className="text-lg sm:text-2xl font-bold">
                {couponAnalysis ? formatCurrency(couponAnalysis.summary.total_sales) : formatCurrency(0)}
              </div>
              <div className="flex items-center text-xs text-muted-foreground">
                <ArrowUpRight className="h-3 w-3 text-green-600 mr-1" />
                Vendido no período
              </div>
            </>
          )}
        </CardContent>
        <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-transparent pointer-events-none"></div>
      </Card>

      {/* Commission Card */}
      <Card className="relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 depth-2 depth-transition depth-hover-3" style={{ animationDelay: "100ms" }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            Sua Comissão
            <button
              type="button"
              onClick={() => setCommissionHelpOpen(true)}
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {couponLoading && !couponAnalysis ? (
            <div className="space-y-2">
              <div className="h-8 w-24 bg-muted rounded animate-pulse"></div>
              <div className="h-3 w-20 bg-muted rounded animate-pulse"></div>
            </div>
          ) : (
            <>
              <div className="text-lg sm:text-2xl font-bold">
                {couponAnalysis ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">
                        {formatCurrency(
                          calculateCommission(
                            couponAnalysis.summary.total_sales ?? 0,
                            couponAnalysis.orders.completed.reduce((sum, order) => sum + (order.shipping_total || 0), 0),
                            commissionRate
                          )
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={8}>{getCommissionTooltip()}</TooltipContent>
                  </Tooltip>
                ) : (
                  formatCurrency(0)
                )}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                {couponAnalysis ? (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help flex items-center gap-1">
                          {getTierName(commissionRate)} ({getCommissionPercentage(commissionRate)})
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent sideOffset={8} className="max-w-xs">
                        <p className="font-medium mb-1">Como funciona:</p>
                        <p className="text-xs">{getTierTooltip()}</p>
                        <p className="text-xs mt-2 pt-2 border-t">
                          Sua comissão é calculada sobre o valor após desconto do frete.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    {getTierName()} ({getCommissionPercentage()})
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3 w-3 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent sideOffset={8} className="max-w-xs">
                        <p className="text-xs">{getTierTooltip()}</p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
              </div>
            </>
          )}
        </CardContent>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent pointer-events-none"></div>
      </Card>

      {/* Sales Count Card */}
        <Card className="relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 depth-2 depth-transition depth-hover-3" style={{ animationDelay: "200ms" }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Vendas Concluídas</CardTitle>
          <Target className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {couponLoading && !couponAnalysis ? (
            <div className="space-y-2">
              <div className="h-8 w-16 bg-muted rounded animate-pulse"></div>
              <div className="h-3 w-32 bg-muted rounded animate-pulse"></div>
            </div>
          ) : (
            <>
              <div className="text-lg sm:text-2xl font-bold">{couponAnalysis ? couponAnalysis.orders.completed.length : 0}</div>
              <div className="text-xs text-muted-foreground">
                ({couponAnalysis ? couponAnalysis.orders.other.length : 0} pendentes)
              </div>
            </>
          )}
        </CardContent>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent pointer-events-none"></div>
      </Card>

      {/* Total Available Card */}
        <Card className="relative overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 depth-2 depth-transition depth-hover-3" style={{ animationDelay: "300ms" }}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Disponível</CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {availableBalance === null ? (
            <div className="space-y-2">
              <div className="h-8 w-24 bg-muted rounded animate-pulse"></div>
              <div className="h-3 w-20 bg-muted rounded animate-pulse"></div>
            </div>
          ) : (
            <>
              <div className="text-lg sm:text-2xl font-bold">
                {formatCurrency(availableBalance)}
              </div>
              <Button
                variant="link"
                size="sm"
                onClick={onNavigateToWallet}
                className="p-0 h-auto text-xs text-primary hover:text-primary/80"
              >
                Ir para Carteira →
              </Button>
            </>
          )}
        </CardContent>
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-transparent pointer-events-none"></div>
      </Card>
    </div>

      {/* Commission Help Dialog */}
      <Dialog open={commissionHelpOpen} onOpenChange={setCommissionHelpOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              Como é calculada a comissão?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">Sua comissão é calculada usando a seguinte fórmula:</p>
            <div className="bg-muted p-4 rounded-lg">
              <p className="font-mono text-sm">
                (Vendas Totais - Taxas e Frete) × Comissão
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <p className="font-semibold mb-1">Sua comissão:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>
                    <strong>10%</strong> para todos, sem valor mínimo de vendas
                  </li>
                  <li>
                    <strong>15%</strong> para os <strong>5 maiores vendedores</strong> do mês
                  </li>
                </ul>
              </div>
              <p className="text-muted-foreground text-sm">
                Entrou no top 5 uma vez? Os 15% são seus para sempre — mesmo que você saia do top 5
                nos meses seguintes.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
